// Bridge between the Capacitor "TimerSurface" plugin (native wrapper only)
// and TimerModal. No-ops when running in a regular browser PWA.
//
// The whole point of this facade is to hand the native side ABSOLUTE clock
// values instead of a ticking counter: title, targetDurationMs,
// accumulatedDurationMs (time banked before the current run) and
// startedAtEpochMs (wall-clock epoch ms the current run started, or null
// when paused/stopped). Native derives "now" itself from Date/NSDate, so a
// backgrounded process or a suspended app extension can never drift — there
// is nothing to resume ticking, only a fixed instant to compute against.
//
// Two very different native mechanisms sit behind this one JS surface:
//   - Android: system Picture-in-Picture with a native timer overlay, because
//     a paused WebView cannot reliably repaint or play the completion sound.
//   - iOS: Apple explicitly reserves its picture-in-picture API for actual
//     video playback — using it to float an arbitrary UI is against the
//     platform's intended use and a plausible App Review rejection. There is
//     no floating-window API for ordinary apps on iOS. The policy-safe
//     equivalent for "glanceable while backgrounded" is an ActivityKit Live
//     Activity (Dynamic Island + Lock Screen), backed by a local notification
//     for the completion ding. See the KataTimerActivity widget extension.
//
// Both platforms are reached through the same three calls below; TimerModal
// does not need to know which one it's talking to.

function isNative() {
  return (
    typeof window !== 'undefined' &&
    !!window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === 'function' &&
    window.Capacitor.isNativePlatform()
  );
}

function getPlugin() {
  if (!isNative()) return null;
  return window.Capacitor?.Plugins?.TimerSurface || null;
}

// Normalizes caller state into the exact wire payload native expects.
// `startedAtEpochMs` defaults to null (rather than undefined) so plugin
// bridges that reject undefined keys still get a well-formed message.
function toPayload({ title, targetDurationMs, accumulatedDurationMs, startedAtEpochMs, running }) {
  return {
    title: title || '',
    targetDurationMs: targetDurationMs || 0,
    accumulatedDurationMs: accumulatedDurationMs || 0,
    startedAtEpochMs: startedAtEpochMs ?? null,
    running: !!running,
  };
}

async function callPlugin(method, state) {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'not-native' };
  try {
    if (state === undefined) {
      await plugin[method]();
    } else {
      await plugin[method](toPayload(state));
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || 'error' };
  }
}

// Begins showing the native surface. Call once per timer "session"
// (TimerModal mount reaching running for the first time).
function start(state) {
  return callPlugin('start', state);
}

// Updates the already-visible surface on a meaningful state transition
// (pause/resume, reset, retarget). Never call this per animation frame —
// native recomputes the live readout itself from the absolute timestamps.
function update(state) {
  return callPlugin('update', state);
}

// Tears down the native surface entirely (modal closed/saved/unmounted).
function stop() {
  return callPlugin('stop');
}

// The foreground HTML audio already handled this target. Native keeps the
// timer surface visible, but must not replay the ding if PiP/backgrounding
// happens after completion.
function markCompletionHandled() {
  return callPlugin('markCompletionHandled');
}

// Subscribes to native surface lifecycle events, currently just
// 'pipModeChanged' ({ isInPip: boolean }) on Android. Returns a handle with
// remove(); a no-op handle when there is no native plugin to listen to.
function addListener(eventName, handler) {
  const plugin = getPlugin();
  if (!plugin || typeof plugin.addListener !== 'function') {
    return { remove: () => {} };
  }
  try {
    const handle = plugin.addListener(eventName, handler);
    return {
      remove: () => {
        if (typeof handle?.then === 'function') {
          return handle.then(subscription => subscription?.remove?.()).catch(() => {});
        }
        return handle?.remove?.();
      },
    };
  } catch (err) {
    return { remove: () => {} };
  }
}

// Recipe timers use a separate multi-timer native channel. Never route these
// through start/update/stop, which belong exclusively to the workout modal.
async function updateRecipeTimers(payload) {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'not-native' };
  if (typeof plugin.updateRecipeTimers !== 'function') return { ok: false, reason: 'not-supported' };
  try {
    const result = await plugin.updateRecipeTimers(payload);
    return { ok: true, ...(result && typeof result === 'object' ? result : {}) };
  } catch (err) {
    return { ok: false, reason: err?.message || 'error' };
  }
}

// Sign-out/scope changes cancel recipe alarms only, never workout surfaces.
async function stopRecipeTimers(payload) {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'not-native' };
  if (typeof plugin.stopRecipeTimers !== 'function') return { ok: false, reason: 'not-supported' };
  try {
    await plugin.stopRecipeTimers(payload);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || 'error' };
  }
}

const TimerSurfaceService = {
  updateRecipeTimers,
  stopRecipeTimers,
  isNative,
  start,
  update,
  stop,
  markCompletionHandled,
  addListener,
};

export default TimerSurfaceService;
